import * as THREE from "three";
import cdt2d from "cdt2d";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import { toRaw } from "vue";
import { DragControls } from "three/examples/jsm/controls/DragControls";
import API from "@/api/API";
import {
  MIDPOINT_COLOR,
  POINT_COLOR,
  SELECTED_POINT_COLOR,
  BLACK,
  SOLAR_POINT_COLOR,
  MEASUREMENT_POINT_COLOR,
  RENDERING_ORDER,
} from "../constants";

export const createReactiveMidPoint = function (
  startPoint,
  endPoint,
  color = MIDPOINT_COLOR
) {
  const midPoint = new THREE.Vector3().lerpVectors(
    startPoint.position,
    endPoint.position,
    0.5
  );

  const pointGroup = new THREE.Group();

  const dotMaterial = new THREE.MeshBasicMaterial({
    color: color,
    transparent: true,
  });

  const wrapper = new THREE.Mesh(this.dotGeometry, dotMaterial);
  wrapper.position.copy(midPoint);

  wrapper.material.depthTest = false;
  wrapper.renderOrder = RENDERING_ORDER.OUTER_POINT;

  const blackDotMaterial = new THREE.MeshBasicMaterial({
    color: BLACK,
    transparent: true,
  });
  const centerDot = new THREE.Mesh(this.dotGeometry, blackDotMaterial);
  centerDot.position.copy(midPoint);
  centerDot.material.depthTest = false;
  centerDot.renderOrder = RENDERING_ORDER.INNER_POINT;

  pointGroup.add(wrapper);
  pointGroup.add(centerDot);
  this.scene.add(pointGroup);

  return pointGroup;
};

export const updateMidPointPosition = function (point, startPoint, endPoint) {
  const newPosition = new THREE.Vector3().lerpVectors(
    startPoint,
    endPoint,
    0.5
  );

  const [centerDot, wrapper] = point.children;
  wrapper.position.copy(newPosition);
  centerDot.position.copy(newPosition);
};

export const createNonReactivePoint = function (
  referencePoint,
  color = MIDPOINT_COLOR
) {
  const dotMaterial = new THREE.MeshBasicMaterial({
    color: color,
  });

  const tempPoint = new THREE.Mesh(this.dotGeometry, dotMaterial);
  tempPoint.position.x = referencePoint.x;
  tempPoint.position.y = referencePoint.y;
  tempPoint.position.z = referencePoint.z;

  return tempPoint;
};

export const createNonReactiveAreaPoint = function (
  referencePoint,
  color = MIDPOINT_COLOR
) {
  const pointGroup = new THREE.Group();
  pointGroup.position.copy(referencePoint);

  const dotMaterial = new THREE.MeshBasicMaterial({
    color: BLACK,
  });

  const wrapper = new THREE.Mesh(this.dotGeometry, dotMaterial);

  const innerDotMaterial = new THREE.MeshBasicMaterial({
    color: color,
  });
  const innerDot = new THREE.Mesh(this.dotGeometry, innerDotMaterial);

  pointGroup.add(wrapper);
  pointGroup.add(innerDot);

  return pointGroup;
};

export const resetPointsColorForMeasurementAreas = function () {
  this.measurementAreas[this.measurementAreas.length - 1].points.forEach(
    (point) => point.setPointColor(POINT_COLOR)
  );
};

export const checkForClosedArea = function (area) {
  let pointIntersects = this.raycaster.intersectObject(area.points[0]);

  if (pointIntersects.length > 0 || area.closeArea) return true;
  return false;
};

export const detectMeasurementAreaIntersection = function () {
  let intersects = this.raycaster.intersectObjects(
    this.measurementAreas.filter((area) => area.plane).map((area) => area.plane)
  );
  if (intersects.length > 0) return intersects[0].object;
  return false;
};

export const showMeasurementAreaTrash = function (object) {
  const area = this.measurementAreas.find(
    (area) => area.plane.uuid === object.uuid
  );
  const label = area.label;
  const secondColumn = label.element.children[1];
  secondColumn.style = "margin: auto; margin-left: 8px;";
};

export const hideMeasurementAreaTrash = function (object) {
  const area = this.measurementAreas.find(
    (area) => area.plane.uuid === object.uuid
  );
  const label = area.label;
  const secondColumn = label.element.children[1];
  secondColumn.style = "margin: auto; margin-left: 8px; display: none;";
};

export const removeDashedLine = function () {
  if (this.dashedMeasurementLine) {
    this.scene.remove(this.dashedMeasurementLine);
    this.dashedMeasurementLine.children.forEach((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
  }
};
export const checkForComplexArea = function (area) {
  const points = area.points;
  const triangleIndices = this.getTriangleIndices(
    points,
    this.getAxisDifferences(points.map((point) => point.position))
  );
  if (triangleIndices.length === 0) return true;
  return false;
};

export const selectMeasurementArea = function (event) {
  event.preventDefault();

  if (event.target.tagName !== "CANVAS") return;

  this.setMousePosition(event);

  const measurementArea = this.detectMeasurementAreaIntersection();

  if (measurementArea) {
    this.measurementAreas.forEach((area) =>
      this.unselectMeasurementArea(area.plane)
    );
    this.selectedMeasurementArea = measurementArea;
    if (measurementArea.material.opacity !== 0)
      measurementArea.material.opacity = 0.7;
    this.showMeasurementAreaTrash(measurementArea);
  } else {
    if (this.selectedMeasurementArea) {
      if (this.selectedMeasurementArea.material.opacity !== 0)
        this.selectedMeasurementArea.material.opacity = 0.5;
      this.hideMeasurementAreaTrash(this.selectedMeasurementArea);
    }
  }
};

export const unselectMeasurementArea = function (area) {
  if (area) {
    area.material.opacity = 0.5;
    this.hideMeasurementAreaTrash(area);
  }
};

export const reAddMeasurementPoint = function () {
  document.removeEventListener("click", this.reAddMeasurementPoint, false);
  document.addEventListener("click", this.addMeasurementPoint, false);
};

export const addMeasurementPoint = function (event) {
  event.preventDefault();

  if (this.disableClick(event)) return;

  this.setMousePosition(event);

  let newFixedLine = null;
  let midPoint = null;
  let oldTempLine = null;
  let oldTempLabel = null;
  let oldFirstPoint = null;
  let lastArea = null;

  // replace dashed line with solid line
  if (
    this.measurementAreas.length > 0 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    lastArea = this.measurementAreas[this.measurementAreas.length - 1];
    oldFirstPoint = lastArea.firstPoint;

    if (lastArea.tempLine) {
      oldTempLine = lastArea.tempLine;
      oldTempLabel = lastArea.tempLabel;

      const solidLinePoints = [
        lastArea.firstPoint.position,
        this.checkForClosedArea(lastArea)
          ? lastArea.points[0].position
          : lastArea.tempPoint.position,
      ];
      let solidLine = this.createReactiveThickLine(solidLinePoints, 4.0);

      this.removeDashedLine();

      this.scene.add(solidLine);

      midPoint = this.createReactiveMidPoint(
        lastArea.firstPoint,
        this.checkForClosedArea(lastArea)
          ? lastArea.points[0]
          : lastArea.tempPoint
      );

      lastArea.tempLine = null;
      if (lastArea.lines) {
        newFixedLine = {
          line: solidLine,
          firstPoint: lastArea.firstPoint,
          secondPoint: this.checkForClosedArea(lastArea)
            ? lastArea.points[0]
            : lastArea.tempPoint,
          label: lastArea.tempLabel,
          midPoint: midPoint,
        };
        lastArea.lines.push(newFixedLine);
      } else {
        newFixedLine = {
          line: solidLine,
          firstPoint: lastArea.firstPoint,
          secondPoint: this.checkForClosedArea(lastArea)
            ? lastArea.points[0]
            : lastArea.tempPoint,
          label: lastArea.tempLabel,
          midPoint: midPoint,
        };
        lastArea.lines = [newFixedLine];
      }
    }
  }

  // check for closed area
  if (
    this.measurementAreas.length > 0 &&
    this.measurementAreas[this.measurementAreas.length - 1]?.points.length >
      2 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    if (
      this.checkForClosedArea(
        this.measurementAreas[this.measurementAreas.length - 1]
      )
    ) {
      this.measurementAreas[this.measurementAreas.length - 1].complex =
        this.checkForComplexArea(
          this.measurementAreas[this.measurementAreas.length - 1]
        );
      this.drawMeasurementAreaPlane(
        this.measurementAreas[this.measurementAreas.length - 1]?.points,
        true
      );
      this.toggleActive(0);
      return;
    }
  }

  // check for duplicate points
  if (
    this.measurementAreas.length > 0 &&
    this.measurementAreas[this.measurementAreas.length - 1]?.points.length >
      0 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    let pointIntersects = this.raycaster.intersectObjects(
      this.measurementAreas[this.measurementAreas.length - 1].points
    );
    if (pointIntersects.length > 0) return;
  }

  let intersects = this.raycaster.intersectObject(this.modelObject.children[0]);

  // mouse doesn't intersect the model
  if (intersects.length < 1) return;

  // disable point placement when drag is on
  if (this.dragOn) return;

  let o = intersects[0];
  let pIntersect = o.point.clone();
  this.scene.worldToLocal(pIntersect);

  const dot = this.createReactivePoint(
    pIntersect,
    true,
    this.isFirstMeasurementPoint
  );

  const tempPoint = this.createNonReactivePoint(pIntersect);

  if (
    this.measurementAreas.length > 0 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    this.measurementAreas[this.measurementAreas.length - 1].points.push(dot);
  } else {
    this.measurementAreas.push({
      points: [dot],
      closed: false,
    });
  }

  let measurementArea = this.measurementAreas[this.measurementAreas.length - 1];

  measurementArea.firstPoint = dot;
  measurementArea.tempPoint = tempPoint;

  if (
    measurementArea.lines &&
    measurementArea.lines[measurementArea.lines.length - 1].firstPoint
  ) {
    measurementArea.lines[measurementArea.lines.length - 1].secondPoint = dot;
  }
  this.camera.lookAt(measurementArea.firstPoint.position);

  const firstPoint = new THREE.Vector3();
  const secondPoint = new THREE.Vector3();
  measurementArea.firstPoint.getWorldPosition(firstPoint);
  measurementArea.tempPoint.getWorldPosition(secondPoint);
  let points = [firstPoint, secondPoint];

  let newLine = this.createReactiveThickLine(points, 4.0, true);
  this.dashedMeasurementLine = newLine;
  measurementArea.tempLine = newLine;

  let newLabel = this.createLabelBetweenTwoPoints(
    firstPoint,
    secondPoint,
    0.5,
    false,
    null
  );
  measurementArea.tempLabel = newLabel;

  this.scene.add(dot);

  const area = this.measurementAreas[this.measurementAreas.length - 1];
  const label =
    lastArea && lastArea.lines.length > 0 ? lastArea.tempLabel : null;
  const line = newFixedLine ? newFixedLine.line : null;

  if (area.points.length > 1)
    // add point undo callback function
    this.undoStack.push({
      action: "ADD_POINT",
      area: area,
      point: dot,
      line: line,
      midPoint: midPoint,
      oldTempLine: oldTempLine,
      oldFirstPoint: oldFirstPoint,
      newTempLine: newLine,
      oldTempLabel: oldTempLabel,
      newTempLabel: newLabel,
      fixedLine: newFixedLine,
    });
  this.resetRedoStack();
};

export const stickMousePointerToDotForMeasurementArea = function (event) {
  // disable point placing when clicking outside the model
  if (event.target.tagName !== "CANVAS") return;

  this.setMousePosition(event);

  if (
    this.measurementAreas.length === 0 ||
    this.measurementAreas[this.measurementAreas.length - 1].points.length === 0
  )
    return;

  let intersects = this.raycaster.intersectObject(this.modelObject.children[0]);
  if (intersects.length < 1) return;
  let o = intersects[0];
  let pIntersect = o.point.clone();
  this.scene.worldToLocal(pIntersect);

  const currentMeasurementArea =
    this.measurementAreas[this.measurementAreas.length - 1];

  const firstDot = currentMeasurementArea?.points[0];
  const secondDot = currentMeasurementArea?.points[1];
  const thirdDot = currentMeasurementArea?.points[2];
  const lastDot =
    currentMeasurementArea?.points[currentMeasurementArea.points.length - 1];

  const distance = firstDot.position.distanceTo(pIntersect);
  const threshold = 0.25;

  if (firstDot && secondDot && thirdDot) {
    if (distance <= threshold) {
      this.selectedPoint = firstDot;
      this.renderer.domElement.style.cursor = `none`;
      this.measurementAreas[this.measurementAreas.length - 1].closeArea = true;
      this.selectedPoint.setPointColor(SELECTED_POINT_COLOR);
      this.dashedMeasurementLine.material.color.setHex(SELECTED_POINT_COLOR);
      this.showSnapIcon();

      if (this.measurementAreaEndingLine)
        this.measurementAreaEndingLine.visible = false;
      this.inMagenticField = true;

      const firstPoint = new THREE.Vector3();
      const secondPoint = new THREE.Vector3();
      firstDot.getWorldPosition(firstPoint);
      lastDot.getWorldPosition(secondPoint);
      let points = [firstPoint, secondPoint];
      this.updateLinePosition(this.dashedMeasurementLine, points);
    } else {
      if (this.detectMeasurementAreaIntersection()) {
        this.renderer.domElement.style.cursor = `default`;
      } else {
        this.changeCursorToCrosshair();
      }
      this.measurementAreas[this.measurementAreas.length - 1].closeArea = false;
      if (this.selectedPoint) {
        this.selectedPoint.setPointColor(POINT_COLOR);
        this.dashedMeasurementLine.material.color.setHex(POINT_COLOR);
        if (this.measurementAreaEndingLine)
          this.measurementAreaEndingLine.visible = true;
        this.inMagenticField = false;
        this.hideSnapIcon();
        this.selectedPoint = null;
      }
    }
  } else {
    if (this.detectMeasurementAreaIntersection()) {
      this.renderer.domElement.style.cursor = `default`;
    } else {
      this.changeCursorToCrosshair();
    }
    if (this.selectedPoint) {
      this.selectedPoint.setPointColor(POINT_COLOR);
      this.dashedMeasurementLine.material.color.setHex(POINT_COLOR);
      if (this.measurementAreaEndingLine)
        this.measurementAreaEndingLine.visible = true;
      this.inMagenticField = false;
      this.hideSnapIcon();
      this.selectedPoint = null;
    }
  }
};

export const updatePreliminaryPointPositionForMeasurementArea = function (
  intersects
) {
  if (
    this.measurementAreas.length > 0 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed &&
    !this.inMagenticField
  ) {
    if (intersects.length < 1) return;
    let o = intersects[0];
    let pIntersect = o.point.clone();
    this.scene.worldToLocal(pIntersect);
    const measurementArea =
      this.measurementAreas[this.measurementAreas.length - 1];
    let marker = measurementArea.tempPoint;
    marker.position.x = pIntersect.x;
    marker.position.y = pIntersect.y;
    marker.position.z = pIntersect.z;
    const firstPoint = new THREE.Vector3();
    const secondPoint = new THREE.Vector3();
    measurementArea.firstPoint.getWorldPosition(firstPoint);
    measurementArea.tempPoint.getWorldPosition(secondPoint);
    let points = [firstPoint, secondPoint];

    if (this.dashedMeasurementLine) {
      this.updateLinePosition(this.dashedMeasurementLine, points);
    }

    const line = this.dashedMeasurementLine;
    const lineObject = this.scene.getObjectById(line.id);
    if (!lineObject) this.scene.add(toRaw(line));

    const label = measurementArea.tempLabel;
    const distance = this.updateLabelBetweenTwoPoints(
      label,
      firstPoint,
      secondPoint,
      0.5,
      true
    );
    const object = this.scene.getObjectById(label.id);
    if (!object && distance > 0.5) {
      this.scene.add(toRaw(label));
    }

    const numPoints = measurementArea.points.length;
    if (numPoints > 2) {
      const measurementAreaFirstDot = new THREE.Vector3();
      measurementArea.points[0].getWorldPosition(measurementAreaFirstDot);
      const endingPoints = [measurementAreaFirstDot, secondPoint];

      if (this.measurementAreaEndingLine) {
        this.updateLinePosition(this.measurementAreaEndingLine, endingPoints);
      } else {
        let newDottedLine = this.createReactiveThickLine(
          endingPoints,
          4.0,
          true,
          true
        );
        this.measurementAreaEndingLine = newDottedLine;
        this.scene.add(newDottedLine);
      }
    }
  }
};

export const checkLabelsOverlapWithMidpoints = function (area) {
  for (let line of area.lines) {
    const overlaps = this.checkLabelOverlapWithPoint(
      line.label,
      line.midPoint.children[0]
    );
    console.log(overlaps);
  }
};

export const drawMeasurementAreaPlane = async function (
  points,
  createArea = true
) {
  const currentMeasurementArea =
    this.measurementAreas[this.measurementAreas.length - 1];

  // this.checkLabelsOverlapWithMidpoints(currentMeasurementArea);
  this.resetPointsColorForMeasurementAreas();
  this.resetUndoStack();
  this.resetRedoStack();
  this.removeSnapIcon(true);

  const vectorPoints = points.map(
    (point) =>
      new THREE.Vector3(point.position.x, point.position.y, point.position.z)
  );

  const pointsAsArray = vectorPoints.map((point) => [
    point.x,
    point.y,
    point.z,
  ]);

  const flatPoints = [].concat(...pointsAsArray);

  const geometry = new THREE.BufferGeometry();
  const vertices = new Float32Array(flatPoints);

  let indices;

  if (currentMeasurementArea.complex) {
    indices = this.generateIndices(vectorPoints);
  } else {
    const triangleIndices = this.getTriangleIndices(
      currentMeasurementArea.points,
      this.getAxisDifferences(
        currentMeasurementArea.points.map((point) => point.position)
      )
    );
    indices = [].concat(...triangleIndices);
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.Uint16BufferAttribute(indices, 1));

  const material = new THREE.MeshBasicMaterial({
    color: POINT_COLOR,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: currentMeasurementArea.complex ? 0.0 : 0.5,
  });

  this.removeDashedLine();

  if (this.measurementAreaEndingLine)
    this.removeObjectFromScene(this.measurementAreaEndingLine);

  this.measurementAreaEndingLine = null;

  const plane = new THREE.Mesh(geometry, material);
  plane.material.depthTest = false;
  plane.renderOrder = RENDERING_ORDER.MEASUREMENT_PLANE;

  let surfaceArea = "";
  let weightedAngle = "";

  if (!currentMeasurementArea.complex) {
    ({ surfaceArea, weightedAngle } = this.calculateAreaMeasurement(points));
  }

  const areaParagraph = document.createElement("p");
  areaParagraph.textContent = `${surfaceArea}m²`;

  const angleParagraph = document.createElement("p");
  angleParagraph.textContent = `${weightedAngle}°`;

  const trashCan = document.createElement("img");
  trashCan.src = "/assets/model/trash_red.svg";
  trashCan.style = "cursor:pointer; width: 25px; pointer-events: all;";

  trashCan.addEventListener("click", this.removeSelectedMeasurementArea);

  const trashCanDiv = document.createElement("div");
  trashCanDiv.append(trashCan);

  const div = document.createElement("div");
  div.className = "areaLabel row";

  const firstColumn = document.createElement("div");
  const secondColumn = document.createElement("div");

  firstColumn.className = "column";
  firstColumn.append(areaParagraph);
  firstColumn.append(angleParagraph);

  secondColumn.className = "column";
  secondColumn.style = "margin: auto; margin-left: 8px; display: none;";
  secondColumn.append(trashCanDiv);

  div.appendChild(firstColumn);
  div.appendChild(secondColumn);

  let label = new CSS2DObject(div);
  const centerPoint = this.getCenterPointFromVectors(vectorPoints);
  label.position.set(centerPoint.x, centerPoint.y, centerPoint.z);
  label.layers.set(2);
  label.renderOrder = RENDERING_ORDER.MEASUREMENT_AREA_LABEL;
  this.scene.add(label);

  this.measurementAreas[this.measurementAreas.length - 1].plane = plane;
  this.measurementAreas[this.measurementAreas.length - 1].points = points;
  this.measurementAreas[this.measurementAreas.length - 1].expanded = false;
  this.measurementAreas[this.measurementAreas.length - 1].panelSpacing = null;
  this.measurementAreas[
    this.measurementAreas.length - 1
  ].transparencyLevel = 100;
  this.measurementAreas[this.measurementAreas.length - 1].panelType = null;
  this.measurementAreas[this.measurementAreas.length - 1].panels = [];
  this.measurementAreas[this.measurementAreas.length - 1].closed = true;
  this.measurementAreas[
    this.measurementAreas.length - 1
  ].closemeasurementArea = false;
  this.measurementAreas[this.measurementAreas.length - 1].label = label;
  this.measurementAreas[this.measurementAreas.length - 1].indices = indices;
  this.measurementAreas[this.measurementAreas.length - 1].surfaceArea =
    surfaceArea;
  this.measurementAreas[this.measurementAreas.length - 1].angle = weightedAngle;
  this.scene.add(plane);

  const areaPoints = points.map((point) => {
    return {
      x: point.position.x,
      y: point.position.y,
      z: point.position.z,
    };
  });

  if (!this.anonymousUser && createArea) {
    try {
      const { data } = await this.createMeasurementAreaObject(areaPoints);
      this.measurementAreas[this.measurementAreas.length - 1].id = data;
    } catch (e) {}
  }
};

export const getTriangleIndices = function (points, trial = 0) {
  const delPoints = points.map((v) => {
    return [
      trial < 2 ? v.position.x : v.position.y,
      trial < 1 ? v.position.y : v.position.z,
    ];
  });
  let delEdges = [];

  for (var i = 0; i < points.length; i++) {
    const pair = [i, (i + 1) % points.length];
    delEdges.push(pair);
  }
  const triangleIndices = cdt2d(delPoints, delEdges, { exterior: false });
  return triangleIndices;
};

export const getAxisDifferences = function (vectors) {
  const minX = Math.min(...vectors.map((vector) => vector.x));
  const maxX = Math.max(...vectors.map((vector) => vector.x));
  const diffX = maxX - minX;

  const minY = Math.min(...vectors.map((vector) => vector.y));
  const maxY = Math.max(...vectors.map((vector) => vector.y));
  const diffY = maxY - minY;

  const minZ = Math.min(...vectors.map((vector) => vector.z));
  const maxZ = Math.max(...vectors.map((vector) => vector.z));
  const diffZ = maxZ - minZ;

  if (diffX === Math.min(diffX, diffY, diffZ)) {
    return 2;
  } else if (diffY === Math.min(diffX, diffY, diffZ)) {
    return 1;
  } else {
    return 0;
  }
};

export const calculateAreaMeasurement = function (points) {
  const triangleIndices = this.getTriangleIndices(
    points,
    this.getAxisDifferences(points.map((point) => point.position))
  );
  let triangles = [];
  for (let i = 0; i < triangleIndices.length; i++) {
    triangles.push([]);
    for (let j = 0; j < triangleIndices[i].length; j++) {
      triangles[i].push(points[triangleIndices[i][j]]);
    }
  }
  let area = 0;
  let angle = 0;
  for (let i = 0; i < triangles.length; i++) {
    const vertexA = triangles[i][0];
    const vertexB = triangles[i][1];
    const vertexC = triangles[i][2];

    // const triangleMesh = this.createTriangleMesh(vertexA, vertexB, vertexC);
    const triangleArea = this.calculateTriangleArea(
      vertexA.position,
      vertexB.position,
      vertexC.position
    );
    const triangleAngle = this.calculateTriangleAngle(
      vertexA.position,
      vertexB.position,
      vertexC.position
    );
    area = Number(area) + Number(triangleArea);
    angle = Number(angle) + Number(triangleAngle) * Number(triangleArea);
  }
  return {
    surfaceArea: area.toFixed(2),
    weightedAngle: (angle / area).toFixed(2),
  };
};

export const createTriangleMesh = function (vertexA, vertexB, vertexC) {
  var geometry = new THREE.BufferGeometry();

  // Create arrays to hold the vertex positions
  var vertices = new Float32Array([
    vertexA.position.x,
    vertexA.position.y,
    vertexA.position.z,
    vertexB.position.x,
    vertexB.position.y,
    vertexB.position.z,
    vertexC.position.x,
    vertexC.position.y,
    vertexC.position.z,
  ]);

  // Create a face using indices
  var indices = new Uint32Array([0, 1, 2]);

  // Add the vertices and indices to the BufferGeometry
  geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));

  var color = new THREE.Color(Math.random(), Math.random(), Math.random());

  var material = new THREE.MeshBasicMaterial({
    color: color,
    side: THREE.DoubleSide,
  });

  return new THREE.Mesh(geometry, material);
};

export const calculateTriangleAngle = function (vertexA, vertexB, vertexC) {
  const vector1 = new THREE.Vector3().subVectors(vertexB, vertexA);
  const vector2 = new THREE.Vector3().subVectors(vertexC, vertexA);

  const normal = new THREE.Vector3().crossVectors(vector1, vector2).normalize();

  const verticalVector = new THREE.Vector3(0, -1, 0);

  const angle = verticalVector.angleTo(normal);

  let angleDegrees = THREE.MathUtils.radToDeg(angle);
  if (angleDegrees > 90) angleDegrees = 180 - angleDegrees;

  return angleDegrees.toFixed(2);
};

export const removeSelectedMeasurementArea = function () {
  let areaIndex, area;
  for (let i = 0; i < this.measurementAreas.length; i++) {
    let areaObj = this.measurementAreas[i];
    if (areaObj.plane.uuid === this.selectedMeasurementArea.uuid) {
      area = areaObj;
      areaIndex = i;
    }
  }

  this.hideObjectFromScene(this.selectedMeasurementArea);
  this.hideObjectFromScene(area.label);

  for (let point of area.points) {
    this.hideObjectFromScene(point);
  }

  for (let line of area.lines) {
    this.hideObjectFromScene(line.line);
    this.hideObjectFromScene(line.label);
    this.hideObjectFromScene(line.midPoint);
  }

  this.measurementAreas.splice(areaIndex, 1);
  this.deleteMeasurementAreaObject(area.id);

  this.undoStack.push({ action: "DELETE_MEASUREMENT_AREA", area: area });
  this.resetRedoStack();

  this.selectedMeasurementArea = null;
};

export const enableMeasurementPointDragMode = function () {
  for (let area of this.measurementAreas) {
    if (area.dragControls) {
      area.dragControls.dispose();
      area.midPointDragControls.dispose();
    }
    area.dragControls = new DragControls(
      area.points,
      this.camera,
      this.renderer.domElement
    );
    area.dragControls.addEventListener("drag", this.dragMeasurementAreaPoint);
    area.dragControls.addEventListener(
      "dragstart",
      this.dragMeasurementAreaPointStart
    );
    area.dragControls.addEventListener(
      "dragend",
      this.dragMeasurementAreaPointEnd
    );

    area.midPointDragControls = new DragControls(
      area.lines.map((line) => line.midPoint),
      this.camera,
      this.renderer.domElement
    );
    area.midPointDragControls.addEventListener(
      "drag",
      this.dragMeasurementAreaMidPoint
    );
    area.midPointDragControls.addEventListener(
      "dragstart",
      this.dragMeasurementAreaPointStart
    );
    area.midPointDragControls.addEventListener(
      "dragend",
      this.dragMeasurementAreaMidPointEnd
    );
  }
};

export const disableMeasurementPointDragMode = function () {
  for (let area of this.measurementAreas) {
    if (area.dragControls) {
      area.dragControls.deactivate();
      area.midPointDragControls.deactivate();

      area.dragControls.removeEventListener(
        "drag",
        this.dragMeasurementAreaPoint
      );
      area.dragControls.removeEventListener(
        "dragstart",
        this.dragMeasurementAreaPointStart
      );
      area.dragControls.removeEventListener(
        "dragend",
        this.dragMeasurementAreaPointEnd
      );

      area.midPointDragControls.removeEventListener(
        "drag",
        this.dragMeasurementAreaPoint
      );
      area.midPointDragControls.removeEventListener(
        "dragstart",
        this.dragMeasurementAreaPointStart
      );
      area.midPointDragControls.removeEventListener(
        "dragend",
        this.dragMeasurementAreaPointEnd
      );
    }
  }
};

export const dragMeasurementAreaPointStart = function (e) {
  this.cleanNavigationSetup();
  this.disableDefaultNavigation();

  this.unselectMeasurementArea(this.selectedMeasurementArea);
  const draggedPoint = e.object;
  draggedPoint.lastPosition = {
    x: draggedPoint.position.x,
    y: draggedPoint.position.y,
    z: draggedPoint.position.z,
  };
  draggedPoint.originalPosition = {
    x: draggedPoint.position.x,
    y: draggedPoint.position.y,
    z: draggedPoint.position.z,
  };
};

export const dragMeasurementAreaPoint = function (e) {
  this.dragOn = true;
  const draggedPoint = e.object;

  const cameraPosition = this.camera.position.clone();
  const pointPosition = draggedPoint.position.clone();
  const rayDirection = pointPosition.sub(cameraPosition).normalize();

  this.raycaster.set(cameraPosition, rayDirection);
  const intersects = this.raycaster.intersectObject(
    this.modelObject.children[0]
  );

  if (intersects.length > 0) {
    draggedPoint.intersectionPoint = intersects[0].point;

    draggedPoint.position.x = intersects[0].point.x;
    draggedPoint.position.y = intersects[0].point.y;
    draggedPoint.position.z = intersects[0].point.z;
  }

  const mergedPoint = this.checkMergePoints(draggedPoint);
  this.reDrawMeasurementAreaFromPoint(mergedPoint);
  setTimeout(() => (this.dragOn = false));
};

export const dragMeasurementAreaMidPoint = function (e) {
  this.dragOn = true;

  const draggedPoint = e.object;
  let otherPoint = null;

  for (let area of this.measurementAreas) {
    const lines = area.lines;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const pointGroup = line.midPoint;
      const [centerDot, wrapper] = pointGroup.children;
      if (draggedPoint.uuid === centerDot.uuid) {
        otherPoint = wrapper;
      } else if (draggedPoint.uuid === wrapper.uuid) {
        otherPoint = centerDot;
      }
      if (otherPoint) {
        this.currentMeasurementArea = area;
        this.otherPointToRemove = otherPoint;

        const cameraPosition = this.camera.position.clone();
        const pointPosition = draggedPoint.position.clone();
        const rayDirection = pointPosition.sub(cameraPosition).normalize();

        this.raycaster.set(cameraPosition, rayDirection);
        const intersects = this.raycaster.intersectObject(
          this.modelObject.children[0]
        );

        if (intersects.length > 0) {
          draggedPoint.intersectionPoint = intersects[0].point;

          draggedPoint.position.x = intersects[0].point.x;
          draggedPoint.position.y = intersects[0].point.y;
          draggedPoint.position.z = intersects[0].point.z;
        }

        otherPoint.position.x = draggedPoint.position.x;
        otherPoint.position.y = draggedPoint.position.y;
        otherPoint.position.z = draggedPoint.position.z;

        if (!area.midPointAdded) {
          this.hideObjectFromScene(line.line);
          this.hideObjectFromScene(line.label);

          for (let j = 0; j < area.points.length; j++) {
            const point = area.points[j];
            if (point.uuid === line.firstPoint.uuid) {
              const newPointIndex = j + 1;
              area.points.splice(newPointIndex, 0, draggedPoint);
              area.midPointAdded = true;
              break;
            }
          }

          area.lineIndexToRemove = i;

          const firstLine = this.createLineGroup(line.firstPoint, draggedPoint);
          const secondLine = this.createLineGroup(
            draggedPoint,
            line.secondPoint
          );

          area.lines.push(firstLine);
          area.lines.push(secondLine);
        } else {
          this.reDrawMeasurementAreaFromPoint(draggedPoint);
        }
        setTimeout(() => (this.dragOn = false));
        return;
      }
    }
  }
  setTimeout(() => (this.dragOn = false));
};

export const createLineGroup = function (
  firstPoint,
  secondPoint,
  withLabel = true,
  withOffset = false
) {
  const solidLinePoints = [firstPoint.position, secondPoint.position];
  let solidLine = this.createReactiveThickLine(
    solidLinePoints,
    4.0,
    false,
    false,
    withLabel ? MIDPOINT_COLOR : SOLAR_POINT_COLOR
  );
  this.scene.add(solidLine);

  const midPoint = this.createReactiveMidPoint(
    firstPoint,
    secondPoint,
    withLabel ? MIDPOINT_COLOR : SOLAR_POINT_COLOR
  );

  let label = undefined;
  if (withLabel) {
    label = this.createLabelBetweenTwoPoints(
      firstPoint.position,
      secondPoint.position,
      0.5,
      false,
      null,
      withOffset,
      midPoint
    );
    this.scene.add(label);
  }

  return {
    line: solidLine,
    firstPoint: firstPoint,
    secondPoint: secondPoint,
    label: label,
    midPoint: midPoint,
  };
};

export const reDrawMeasurementAreaFromPoint = function (point) {
  let currentMeasurementArea = null;
  for (let area of this.measurementAreas) {
    for (let i = 0; i < area.points.length; i++) {
      let areaPoint = area.points[i];
      if (areaPoint.uuid === point.uuid) {
        currentMeasurementArea = area;
        this.selectedMeasurementArea = currentMeasurementArea.plane;
        break;
      }
    }
  }
  if (!currentMeasurementArea) return;

  for (let i = 0; i < currentMeasurementArea.points.length; i++) {
    if (currentMeasurementArea.points[i].uuid === point.uuid) {
      currentMeasurementArea.points[i] = point;
    }
  }

  currentMeasurementArea.complex = this.checkForComplexArea(
    currentMeasurementArea
  );

  // redraw area outline
  const vectorPoints = currentMeasurementArea.points.map(
    (point) =>
      new THREE.Vector3(point.position.x, point.position.y, point.position.z)
  );

  const pointsAsArray = vectorPoints.map((point) => [
    point.x,
    point.y,
    point.z,
  ]);

  const flatPoints = [].concat(...pointsAsArray);

  const geometry = currentMeasurementArea.plane.geometry;
  const vertices = new Float32Array(flatPoints);

  let indices;

  if (currentMeasurementArea.complex) {
    indices = this.generateIndices(vectorPoints);
  } else {
    const triangleIndices = this.getTriangleIndices(
      currentMeasurementArea.points,
      this.getAxisDifferences(
        currentMeasurementArea.points.map((point) => point.position)
      )
    );
    indices = [].concat(...triangleIndices);
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.Uint16BufferAttribute(indices, 1));

  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  // redraw area lines
  const linesToUpdate = currentMeasurementArea.lines.filter(
    (line) =>
      line.firstPoint.uuid === point.uuid ||
      line.secondPoint.uuid === point.uuid
  );
  for (let line of linesToUpdate) {
    if (line.firstPoint.uuid === point.uuid) line.firstPoint = point;
    else line.secondPoint = point;

    const firstPoint = new THREE.Vector3();
    const secondPoint = new THREE.Vector3();
    line.firstPoint.getWorldPosition(firstPoint);
    line.secondPoint.getWorldPosition(secondPoint);
    let points = [firstPoint, secondPoint];
    this.updateLinePosition(line.line, points);

    const label = line.label;
    this.updateLabelBetweenTwoPoints(label, firstPoint, secondPoint, 0.5, true);

    const midPoint = line.midPoint;
    this.updateMidPointPosition(midPoint, firstPoint, secondPoint);
  }

  // recalculate area measurments
  let surfaceArea = "";
  let weightedAngle = "";

  if (!currentMeasurementArea.complex) {
    ({ surfaceArea, weightedAngle } = this.calculateAreaMeasurement(
      currentMeasurementArea.points
    ));
  }

  currentMeasurementArea.surfaceArea = surfaceArea;
  currentMeasurementArea.angle = weightedAngle;

  const label = currentMeasurementArea.label;
  const centerPoint = this.getCenterPointFromVectors(vectorPoints);
  label.position.set(centerPoint.x, centerPoint.y, centerPoint.z);
  const firstColumn = label.element.children[0];
  const [areaParagraph, angleParagraph] = firstColumn.children;
  areaParagraph.textContent = `${surfaceArea}m²`;
  angleParagraph.textContent = `${weightedAngle}°`;

  // snap dragged point to the mesh
  const cameraPosition = this.camera.position.clone();
  const pointPosition = point.position.clone();
  const rayDirection = pointPosition.sub(cameraPosition).normalize();

  this.raycaster.set(cameraPosition, rayDirection);

  const intersects = this.raycaster.intersectObject(
    this.modelObject.children[0]
  );

  if (intersects.length > 0) {
    point.prevPosition = {
      x: point.position.x,
      y: point.position.y,
      z: point.position.z,
    };
  }

  return currentMeasurementArea;
};

export const dragMeasurementAreaPointEnd = function (event) {
  const point = event.object;

  const cameraPosition = this.camera.position.clone();
  const pointPosition = point.position.clone();
  const rayDirection = pointPosition.sub(cameraPosition).normalize();

  this.raycaster.set(cameraPosition, rayDirection);

  const intersects = this.raycaster.intersectObject(
    this.modelObject.children[0]
  );

  if (intersects.length < 1 && point.prevPosition) {
    point.position.x = point.prevPosition.x;
    point.position.y = point.prevPosition.y;
    point.position.z = point.prevPosition.z;

    this.reDrawMeasurementAreaFromPoint(point);
  }
  document.addEventListener("click", this.selectMeasurementArea, false);

  if (!this.selectedMeasurementArea) return;

  let areaObject;
  for (let area of this.measurementAreas) {
    if (area.plane.uuid === this.selectedMeasurementArea.uuid)
      areaObject = area;
  }

  this.restoreDefaultNavigation();

  this.updateMeasurementAreaObject(
    areaObject.id,
    areaObject.points.map((point) => {
      return {
        x: point.position.x,
        y: point.position.y,
        z: point.position.z,
      };
    })
  );

  let area = this.measurementAreas[this.measurementAreas.length - 1];

  if (this.pointBelongsToArea(point, area))
    this.undoStack.push({
      action: "MOVE_POINT",
      areaType: "MEASUREMENT_AREA",
      area: area,
      point: point,
    });
  this.resetRedoStack();
};

export const dragMeasurementAreaMidPointEnd = function (event) {
  if (!this.currentMeasurementArea) return;

  this.currentMeasurementArea.midPointAdded = false;
  const removedLine = this.currentMeasurementArea.lines.splice(
    this.currentMeasurementArea.lineIndexToRemove,
    1
  )[0];

  const draggedPoint = event.object;

  const cameraPosition = this.camera.position.clone();
  const pointPosition = draggedPoint.position.clone();
  const rayDirection = pointPosition.sub(cameraPosition).normalize();

  this.raycaster.set(cameraPosition, rayDirection);

  const intersects = this.raycaster.intersectObject(
    this.modelObject.children[0]
  );

  if (intersects.length < 1) {
    draggedPoint.position.x = draggedPoint.prevPosition.x;
    draggedPoint.position.y = draggedPoint.prevPosition.y;
    draggedPoint.position.z = draggedPoint.prevPosition.z;

    this.reDrawMeasurementAreaFromPoint(draggedPoint);
  }

  const sceneObject = this.scene.getObjectById(draggedPoint.id);
  this.scene.remove(sceneObject.parent);

  const newPoint = this.createReactivePoint(draggedPoint.position, true);
  this.scene.add(newPoint);

  for (let i = 0; i < this.currentMeasurementArea.points.length; i++) {
    const point = this.currentMeasurementArea.points[i];
    if (point.uuid === draggedPoint.uuid) {
      this.currentMeasurementArea.points[i] = newPoint;
    }
  }
  for (let line of this.currentMeasurementArea.lines) {
    if (line.firstPoint.uuid === draggedPoint.uuid) line.firstPoint = newPoint;
    if (line.secondPoint.uuid === draggedPoint.uuid)
      line.secondPoint = newPoint;
  }

  this.disableMeasurementPointDragMode();
  this.enableMeasurementPointDragMode();
  this.restoreDefaultNavigation();

  this.updateMeasurementAreaObject(
    this.currentMeasurementArea.id,
    this.currentMeasurementArea.points.map((point) => {
      return {
        x: point.position.x,
        y: point.position.y,
        z: point.position.z,
      };
    })
  );

  // Save state for undo
  const newLines = this.currentMeasurementArea.lines.filter(
    (line) =>
      line.firstPoint.id === newPoint.id || line.secondPoint.id === newPoint.id
  );
  let area = this.measurementAreas[this.measurementAreas.length - 1];
  this.undoStack.push({
    action: "MOVE_MID_POINT",
    area: area,
    removedLine: removedLine,
    newLine1: newLines[0],
    newLine2: newLines[1],
    newPoint: newPoint,
    isMeasurement: true,
  });
  this.resetRedoStack();
};

export const createMeasurementAreaObject = async function (points) {
  if (this.sample) return;
  const areaObject = {
    projectId: Number(this.projectId),
    position: points,
  };
  return await API.airteam3DViewer.createMeasurementAreaObject(areaObject);
};

export const updateMeasurementAreaObject = async function (id, points) {
  if (this.sample) return;
  const areaObject = {
    id,
    projectId: Number(this.projectId),
    position: points,
  };
  return await API.airteam3DViewer.updateMeasurementAreaObject(areaObject);
};

export const deleteMeasurementAreaObject = async function (id) {
  if (this.sample) return;
  return await API.airteam3DViewer.deleteObject(id);
};

export const removeUnfinishedAreaMeasurements = function () {
  const currentAreaMeasurement =
    this.measurementAreas[this.measurementAreas.length - 1];
  if (!currentAreaMeasurement || currentAreaMeasurement.closed) return;
  if (this.measurementAreaEndingLine) {
    this.removeObjectFromScene(this.measurementAreaEndingLine);
    this.measurementAreaEndingLine = null;
  }
  this.removeDashedLine();
  const tempLabel = this.scene.getObjectById(
    currentAreaMeasurement.tempLabel.id
  );
  this.scene.remove(tempLabel);

  this.removeSnapIcon(true);
  if (currentAreaMeasurement.points)
    this.removeArrayFromScene(currentAreaMeasurement.points);

  if (currentAreaMeasurement.lines) {
    this.removeArrayFromScene(
      currentAreaMeasurement.lines.map((line) => line.label)
    );
    this.removeArrayFromScene(
      currentAreaMeasurement.lines.map((line) => line.line)
    );
    currentAreaMeasurement.lines
      .map((line) => line.midPoint)
      .forEach((point) => {
        this.removeObjectWithChildrenFromScene(point);
      });
  }

  this.measurementAreas.pop();
};

export const removeLastMeasurementPoint = function (event) {
  const key = event.key;
  if (key !== "Backspace" && key !== "Delete") return;

  if (this.measurementAreas.length === 0) return;
  const currentArea = this.measurementAreas[this.measurementAreas.length - 1];
  if (currentArea.points.length === 0 || currentArea.closed) return;
  if (currentArea.points.length === 1) {
    this.removeUnfinishedAreaMeasurements();
  } else {
    this.undo();
  }
};
